import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Output } from '@angular/core';
import { OperatorsService } from '../operators/operators.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditProjectModalComponent } from './edit-project/edit-proect.modal.component';
import { ViewProjectModalComponent } from './view-project/view-project.modal.component';

@Component({
  selector: 'app-table-operators',
  templateUrl: './table-operators.component.html',
  styleUrls: ['./table-operators.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('180ms ease-in-out')),
    ]),
  ],
})
export class TableOperatorsComponent {
  public cnpj = '';
  public empresa = '';
  public email = '';

  public page = 1;
  public pageSize = 20;
  public total = 0;

  public all: any[] = [];
  public rows: any[] = [];

  public displayedColumns: string[] = ['cnpj', 'empresa', 'email', 'responsavel', 'endereco'];
  public displayedColumnsWithExpand: string[] = ['expand', ...this.displayedColumns];

  public expandedRow: any | null = null;

  public details: { [cnpj: string]: any[] } = {};
  public detailsLoading: { [cnpj: string]: boolean } = {};

  public licensingMap: { [id: string]: string } = {};
  public licensingsLoading = false;
  public licensingsLoaded = false;

  @Output() operatorsLoaded = new EventEmitter<any[]>();

  constructor(private operatorsService: OperatorsService, private modalService: NgbModal) {
    this.load();
  }

  trackById = (_: number, item: any) => item?.id ?? _;

  rowCnpj(row: any): string {
    return (row?.company?.cnpj ?? row?.id ?? '').toString();
  }

  enderecoLabel(row: any): string {
    const cidade = row?.adress?.cidade ?? '';
    const estado = row?.adress?.estado ?? '';
    if (!cidade && !estado) return '-';
    if (cidade && estado) return `${cidade}/${estado}`;
    return `${cidade || estado}`;
  }

  reg(item: any): any {
    return item?.operatorRegistration ?? {};
  }

  licensingLabel(licensingId: any): string {
    const id = this.normalizeLicensingId(licensingId);
    if (!id) return '-';
    return this.licensingMap[id] || '-';
  }

  ensureLicensings(): void {
    if (this.licensingsLoaded || this.licensingsLoading) return;

    this.licensingsLoading = true;

    this.operatorsService.getLicensingRegistration(1000, 1).subscribe({
      next: (value: any) => {
        const list = (value?.items ?? value?.data ?? value?.result ?? value ?? []) as any[];

        const map: { [id: string]: string } = {};
        for (const item of list) {
          const id = this.normalizeLicensingId(item?.id);
          const name = (item?.description ?? item?.razaoSocial ?? item?.cnpj ?? '').toString().trim();
          if (id && name) map[id] = name;
        }

        this.licensingMap = map;
        this.licensingsLoaded = true;
      },
      error: () => {
        this.licensingMap = {};
        this.licensingsLoaded = true;
      },
      complete: () => {
        this.licensingsLoading = false;
      },
    });
  }

  toggle(row: any): void {
    const isOpen = this.expandedRow === row;
    this.expandedRow = isOpen ? null : row;

    if (!isOpen) {
      this.ensureLicensings();

      const cnpj = this.rowCnpj(row);

      if (cnpj && !this.details[cnpj] && !this.detailsLoading[cnpj]) {
        this.detailsLoading[cnpj] = true;

        this.operatorsService.getAccountListCnpj(cnpj).subscribe({
          next: (value: any) => {
            this.details[cnpj] = (value?.accountLists ?? []) as any[];
          },
          error: () => {
            this.details[cnpj] = [];
            this.detailsLoading[cnpj] = false;
          },
          complete: () => {
            this.detailsLoading[cnpj] = false;
          },
        });
      }
    }
  }

  isExpanded(row: any): boolean {
    return this.expandedRow === row;
  }

  load(refreshDetailsCnpj?: string): void {
    this.operatorsService.getPreAccounts().subscribe({
      next: (list: any) => {
        this.all = [...((list ?? []) as any[])].reverse();
        this.page = 1;
        this.applyFiltersAndPaginate();

        const cnpj = (refreshDetailsCnpj ?? '').toString().trim();
        if (cnpj) {
          this.reloadDetails(cnpj);
        }
      },
      error: () => {
        this.all = [];
        this.rows = [];
        this.total = 0;
        this.expandedRow = null;
        this.operatorsLoaded.emit(this.rows);
      },
    });
  }

  search(a: any, b?: any, c?: any): void {
    if (a && typeof a === 'object') {
      this.cnpj = (a?.cnpj ?? '').toString();
      this.empresa = (a?.empresa ?? '').toString();
      this.email = (a?.email ?? '').toString();
    } else {
      this.cnpj = (a ?? '').toString();
      this.empresa = (b ?? '').toString();
      this.email = (c ?? '').toString();
    }

    this.page = 1;
    this.applyFiltersAndPaginate();
  }

  pageChange(p: number): void {
    this.page = p;
    this.applyFiltersAndPaginate();
  }

  private applyFiltersAndPaginate(): void {
    const cnpj = this.cnpj.trim().toLowerCase();
    const empresa = this.empresa.trim().toLowerCase();
    const email = this.email.trim().toLowerCase();

    const filtered = this.all.filter((x: any) => {
      const vCnpj = (x?.company?.cnpj ?? x?.id ?? '').toString().toLowerCase();
      const vEmpresa = (x?.company?.razaoSocial ?? '').toString().toLowerCase();
      const vEmail = (x?.company?.email ?? '').toString().toLowerCase();

      return (
        (!cnpj || vCnpj.includes(cnpj)) &&
        (!empresa || vEmpresa.includes(empresa)) &&
        (!email || vEmail.includes(email))
      );
    });

    this.total = filtered.length;

    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.rows = filtered.slice(start, end);

    this.expandedRow = null;
    this.operatorsLoaded.emit(this.rows);
  }

  innerEdit(item: any, ev?: Event): void {
    ev?.stopPropagation();

    const modalRef = this.modalService.open(EditProjectModalComponent, {
      size: 'xl',
      windowClass: 'modal-wide',
      centered: true,
      backdrop: 'static',
      keyboard: true,
    });

    modalRef.componentInstance.project = item;

    modalRef.closed.subscribe((result: any) => {
      if (result === 'saved') {
        const cnpj = (item?.company?.cnpj ?? item?.operatorRegistration?.company?.cnpj ?? '').toString().trim();
        if (cnpj) this.reloadDetails(cnpj);
      }
    });
  }

  reloadDetails(cnpj: string): void {
    if (!cnpj) return;

    this.details[cnpj] = [];
    this.detailsLoading[cnpj] = true;

    this.operatorsService.getAccountListCnpj(cnpj).subscribe({
      next: (value: any) => {
        this.details[cnpj] = (value?.accountLists ?? []) as any[];
      },
      error: () => {
        this.details[cnpj] = [];
        this.detailsLoading[cnpj] = false;
      },
      complete: () => {
        this.detailsLoading[cnpj] = false;
      },
    });
  }

  innerView(item: any, ev?: Event): void {
    ev?.stopPropagation();

    const modalRef = this.modalService.open(ViewProjectModalComponent, {
      size: 'xl',
      windowClass: 'modal-wide',
      centered: true,
      backdrop: 'static',
      keyboard: true,
    });

    modalRef.componentInstance.project = item;
  }

  private normalizeLicensingId(value: any): string {
    const id = (value ?? '').toString().trim();
    if (!id || id === '00000000-0000-0000-0000-000000000000') {
      return '';
    }

    return id;
  }
}
